<template>
  <base-view-layout class="contract-show">
    <template #header v-if="contractInformation">
      <div class="contract-info-section">
        <div class="contract-contact-info">
          <div class="contract-info w-full">
            <router-link
              class="text-red hover-bold"
              :to="{
                name: 'projects-show',
                params: { id: contractInformation.project_id }
              }"
            >
              <h1>{{ contractInformation.project_name }}</h1>
            </router-link>
            <h2 class="uppercase">
              {{ contractInformation.name }}
            </h2>
            <p class="uppercase text-sm" v-if="$mq === 'sm'">
              <i
                class="fa-fw text-lg text-blue"
                :class="iconMap[contractInformation.type]"
              />
              {{ contractInformation.type }}
            </p>
          </div>
          <div class="customer-info mt-4">
            <p class="md:text-lg leading-tight mt-1">
              {{ contractInformation.place.address_1 }}
            </p>
            <p
              class="md:text-lg leading-tight"
              v-if="contractInformation.place.address_2"
            >
              {{ contractInformation.place.address_2 }}
            </p>
            <p class="md:text-lg leading-tight">
              {{ contractInformation.place.cityStateZip }}
              <a
                class="ml-1 text-red hover-bold"
                :href="generateGoogleMapsUrl(contractInformation.place)"
              >
                <i class="fal fa-map-marker-alt"></i>
              </a>
            </p>
            <div class="contact-info flex mt-2 md:text-lg">
              <div class="mr-2 font-bold">Primary Contact:</div>
              <div>
                <p>
                  {{ contractInformation.primary_contact.name }}
                </p>
                <p>
                  <a
                    class="text-red hover-bold"
                    :href="`mailto:${contractInformation.email}`"
                  >
                    {{ contractInformation.email }}
                  </a>
                </p>
                <p>
                  {{ contractInformation.primaryPhoneNumber }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <bdg-map :place="contractInformation.place" />
        <div class="billing-address-container">
          <template v-if="contractInformation.billing_place">
            <bdg-aside title="Billing address" />
            <address-cell
              :place="contractInformation.billing_place"
              v-if="contractInformation.billing_place"
            />
          </template>
        </div>
        <div class="contract-actions" v-if="$mq !== 'sm'">
          <p class="uppercase text-sm">
            <i
              class="fa-fw text-lg text-blue"
              :class="iconMap[contractInformation.type]"
            />
            {{ contractInformation.type }}
          </p>
        </div>
      </div>
    </template>
    <template #body>
      <stacked-tabs :tabs="tabs" no-tab-classes />
      <router-view />
    </template>
  </base-view-layout>
</template>

<script>
import BdgMap from "../../elements/BdgMap";
// import Callout from "../../elements/Callout";
import StackedTabs from "../../elements/StackedTabs";
import { Contract } from "../../models/Contract";
import BaseViewLayout from "../layouts/BaseViewLayout";
import ContractForm from "./ContractForm";
import { ProjectShowIcons } from "../../js-data/icon-maps";
import GenericDeleteModal from "../../components/modals/GenericDeleteModal";
import { ContractTabs } from "../../constants/contract-enums";
import AddressCell from "../../elements/table-view/special-cells/AddressCell";
import BdgAside from "../../elements/BdgAside";

export default {
  name: "ContractShow",
  components: {
    BdgAside,
    AddressCell,
    BaseViewLayout,
    StackedTabs,
    // Callout,
    BdgMap
  },
  props: {
    id: [String, Number],
    contract: {
      type: Contract
    }
  },
  data() {
    return {
      iconMap: ProjectShowIcons,
      callouts: [
        "1 delivery cancelled",
        "1 overdue work order",
        "2 new notes added"
      ]
    };
  },
  computed: {
    userType() {
      return this.$store.state.userType;
    },
    isGettingContracts() {
      return this.$store.state.Contracts.isGettingContracts;
    },
    contractInformation() {
      return this.$store.state.Contracts.contract;
    },
    tabs() {
      const tabs = this.$store.state.Contracts.showTabs;
      return this.userType === "admin"
        ? tabs
        : tabs.filter(t => {
            return (
              [
                ContractTabs.CONTACTS,
                ContractTabs.PLANS,
                ContractTabs.PROCUREMENTS,
                ContractTabs.JOB_TRACKING,
                ContractTabs.WORK_ORDERS,
                ContractTabs.NOTES
              ].indexOf(t.slug) > -1
            );
          });
    }
  },
  created() {
    this.$store.dispatch("getContract", this.id);
  },
  methods: {
    editContract() {
      this.$store.commit("setCreateEditComponentParams", {
        createEditComponent: ContractForm,
        createEditComponentData: this.contractInformation,
        createEditComponentIsShown: true
      });
    },
    deleteContract() {
      this.$store.commit("setConfirmationModalParams", {
        type: "delete",
        headerText: "Delete Confirmation",
        bodyComponent: GenericDeleteModal,
        bodyComponentData: {
          entityName: "contract",
          deleteFunction: () => {
            this.$store
              .dispatch("deleteContract", this.contractInformation)
              .then(res => {
                this.$store.dispatch("handleApiResponse", {
                  responseStatus: res,
                  successText: "Contract deleted successfully.",
                  errorText:
                    "An error occurred while attempting to delete Contract.",
                  afterSuccessFunction: () => {
                    this.$store.dispatch(
                      "getProject",
                      this.contractInformation.project_id
                    );
                    this.$store.commit("resetConfirmationModalParams");
                    this.$router.push({
                      name: "projects-show",
                      params: { id: this.contractInformation.project_id }
                    });
                  }
                });
              });
          }
        },
        isShown: true
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../scss/assets";

.contract-show {
  .contract-info-section {
    @screen md {
      @apply flex;
    }

    .contract-contact-info {
      @screen md {
        @apply order-1;
      }
    }

    .bdg-map {
      @apply w-full-plus-8 mt-6 -mx-4;

      @screen md {
        @apply order-3 m-0;
      }
    }

    .billing-address-container {
      @apply w-full mt-8 mb-2;

      @screen md {
        @apply order-2 m-0;
      }
    }

    .contract-actions {
      @screen md {
        @apply order-4 block text-right;
      }

      .button {
        & + .button {
          @screen md {
            @apply ml-2;
          }
        }
      }
    }

    .contract-contact-info,
    .bdg-map,
    .billing-address-container,
    .contract-actions {
      @screen md {
        @include evenlySpacedFlexChild(4, 2.75rem);
      }
    }
  }
}
</style>
